import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { RxPlus } from 'react-icons/rx'
import { useMemo, useState } from 'react'
import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'
import { columns, Food } from '@/components/nutrition/foodColumns'
import FoodSheet from '@/components/nutrition/foodSheet'
import DeleteAlert from '@/components/DeleteAlert'
import {
  useHasPermission,
  useOrg,
  useUserId,
} from '@/lib/states/session/hooks'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpc } from '@/lib/trpc/client'
import { patchListCache, removeFromList } from '@trainrr/api-client'
import { canDeleteOrgLibraryItem } from '@/lib/libraryAccess'

export const Route = createFileRoute('/_auth/_dashboard/foods')({
  component: FoodList,
})

function FoodList() {
  const org = useOrg()
  const userId = useUserId()
  const canManageLibrary = useHasPermission('library.edit')
  const tableColumns = useMemo(
    () => columns({ userId, canManageLibrary }),
    [userId, canManageLibrary],
  )
  const [open, setOpen] = useState(false)
  const [food, setFood] = useState<Food | undefined>(undefined)
  const [foodToDelete, setFoodToDelete] = useState<Food>()

  const { data: foods, isLoading, error } = trpc.foods.list.useQuery(
    undefined,
    { enabled: Boolean(org?.id) },
  )
  const utils = trpc.useUtils()
  const deleteFood = trpc.foods.delete.useMutation({
    onSuccess: (_data, variables) => {
      patchListCache(utils.foods.list, (old) =>
        removeFromList(old, variables.id),
      )
      setFoodToDelete(undefined)
    },
    onError: (mutationError) => {
      errorToast(mutationError)
    },
  })

  if (error) {
    console.error('error fetching foods', error)
  }

  const handleOnClickEdit = (selected: Food) => {
    if (open) {
      return
    }

    setFood(selected)
    setOpen(true)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setFood(undefined)
    setOpen(nextOpen)
  }

  const handleFoodDeleted = (selected: Food) => {
    if (!canDeleteOrgLibraryItem(selected, { userId, canManageLibrary })) {
      return
    }
    setFoodToDelete(selected)
  }

  return (
    <>
      <DeleteAlert
        title="Delete food"
        description="Are you sure you want to delete this food? This action cannot be undone."
        open={foodToDelete !== undefined}
        onCancel={() => setFoodToDelete(undefined)}
        onDelete={() => {
          if (
            !foodToDelete?.id ||
            !canDeleteOrgLibraryItem(foodToDelete, { userId, canManageLibrary })
          ) {
            setFoodToDelete(undefined)
            return
          }
          deleteFood.mutate({ id: foodToDelete.id })
        }}
      />
      <FoodSheet open={open} onOpenChange={handleOpenChange} food={food} />
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex h-full min-h-0 w-full flex-col md:flex">
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-8 pt-6">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-3xl font-bold tracking-tight">Foods</h2>
              <Button className="gap-2" onClick={() => setOpen(true)}>
                <RxPlus /> Add New
              </Button>
            </div>
            <VirtualizedDataTable
              className="min-h-0 flex-1"
              columns={tableColumns}
              data={(foods as Food[]) ?? []}
              onRowSelected={handleOnClickEdit}
              onRowDeleted={handleFoodDeleted}
              showSearchBar
              stickyHeader
              isLoading={isLoading}
              tableContainerClassName="min-h-0 flex-1"
              tableWrapperClassName="h-full min-h-0"
              virtualizedHeight="100%"
              estimateRowHeight={112}
            />
          </div>
        </div>
      </div>
    </>
  )
}
